import { useContext, useMemo } from 'react';
import { Box, Divider, Tab, Tabs } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GridContext } from 'contexts/GridContext';
import { DropsContext } from 'contexts/DropsContext';
import { PedidosContext } from 'contexts/PedidosContext';
import { DocumentosContext } from 'contexts/DocumentosContext';
import { toastWarning } from 'utils/toast';
import { useModal } from 'components/Modals';
import ResumoModal from 'components/Modals/ResumoModal';
import Container from 'components/Container';
import Header from 'components/Header';
import Card from 'components/Card';
import DataView, { useDataViewFilters } from 'components/DataView';
import api from 'services/api';
import ImportModal from './Modals/ImportModal';
import columnOverrides from './columnOverrides';
import getPedidosFilterFields from './filterFields';

const tabsPedido = [
  { value: 'TODOS', label: 'Todos', tipo_pedido: null },
  {
    value: 'MARKETPLACE',
    label: 'Pedidos de marketplace',
    tipo_pedido: 'MARKETPLACE',
  },
  { value: 'NORMAL', label: 'Pedidos próprios', tipo_pedido: 'NORMAL' },
];

const tabPedidoParam = 'tipo_pedido_tab';
const colunasMarketplace = ['marketplace_name', 'marketplace_logistica'];

const tabsSx = {
  minHeight: 32,
  maxWidth: '100%',
  '& .MuiTabs-scroller': {
    overflowX: 'auto !important',
    touchAction: 'pan-x',
  },
  '& .MuiTabs-flexContainer': {
    gap: 3,
  },
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: 999,
  },
  '& .MuiTab-root': {
    minHeight: 32,
    minWidth: 'auto',
    px: 0,
    py: 0,
    color: 'text.secondary',
    transition: 'color 0.2s ease',
  },
  '& .MuiTab-root:hover': {
    color: 'text.primary',
  },
  '& .MuiTab-root.Mui-selected': {
    color: 'primary.main',
  },
};

const Pedidos = () => {
  const rotina = 'Pedidos';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabPedido = tabsPedido.some(
    (tab) => tab.value === searchParams.get(tabPedidoParam)
  )
    ? searchParams.get(tabPedidoParam)
    : 'TODOS';
  const { openModal, closeModal } = useModal();
  const { drops } = useContext(DropsContext);
  const { exportPdf, exportXls } = useContext(GridContext);
  const { pedidos, getPedidos, getLoading } = useContext(PedidosContext);
  const { postDocumentosGenerator } = useContext(DocumentosContext);

  const setFilterSearchParams = (nextSearchParams, options) => {
    const params = new URLSearchParams(nextSearchParams);

    if (tabPedido !== 'TODOS') {
      params.set(tabPedidoParam, tabPedido);
    }

    setSearchParams(params, options);
  };

  const filterFields = useMemo(() => getPedidosFilterFields(drops), [drops]);
  const pedidosGrid = useMemo(() => {
    if (tabPedido !== 'NORMAL') {
      return pedidos;
    }

    return {
      ...pedidos,
      colunas: pedidos?.colunas?.map((coluna) =>
        colunasMarketplace.includes(coluna?.field)
          ? { ...coluna, visible: false }
          : coluna
      ),
    };
  }, [pedidos, tabPedido]);
  const columnVisibilityKey = useMemo(
    () =>
      pedidosGrid?.colunas
        ?.map((coluna) => `${coluna?.field}:${coluna?.visible}`)
        ?.join('|') || '',
    [pedidosGrid?.colunas]
  );

  const { applyFilter, filterForm } = useDataViewFilters({
    fields: filterFields,
    searchParams,
    setSearchParams: setFilterSearchParams,
    onApply: (props) => loadGrid(props),
  });
  const { getValues } = filterForm;

  const getFilter = (filter = getValues(), tab = tabPedido) => {
    const tabConfig = tabsPedido.find((t) => t.value === tab);

    return {
      ...filter,
      tipo_pedido: tabConfig?.tipo_pedido || null,
    };
  };

  const loadGrid = (props = {}, tab = tabPedido) => {
    const filter = props?.filter || getValues();
    const filterWithTipoPedido = getFilter(filter, tab);

    closeModal();

    getPedidos({ ...props, rotina, filter: filterWithTipoPedido });
  };

  const handleTabChange = (_, value) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    if (value === 'TODOS') {
      nextSearchParams.delete(tabPedidoParam);
    } else {
      nextSearchParams.set(tabPedidoParam, value);
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  const print = async (id) => {
    const params = { id };
    const { data } = await api.get(`/Relatorios/Gerar/Documento`, { params });
    const urls = data?.urls || [];
    urls?.forEach((url) => window.open(url, '_blank'));
  };

  const toolbarActions = [
    {
      name: 'Adicionar Pedido',
      icon: 'add',
      placement: 'primary',
      action: () => navigate('/app/Pedidos/Gerar'),
    },
    {
      name: 'Exportar PDF',
      icon: 'file_download',
      action: () =>
        exportPdf({ rotina, filter: getFilter(), order: pedidos?.order }),
    },
    {
      name: 'Exportar XLS',
      icon: 'file_download',
      action: () =>
        exportXls({ rotina, filter: getFilter(), order: pedidos?.order }),
    },
    {
      name: 'Resumo',
      icon: 'summarize',
      action: () =>
        openModal(
          <ResumoModal
            colunas={pedidos?.colunas}
            onSubmit={(group) =>
              navigate('/app/Resumo', {
                state: { rotina, group, filter: getFilter() },
              })
            }
          />
        ),
    },
    {
      name: 'Gerar Nota Fiscal',
      icon: 'post_add',
      action: (documentos) => {
        if (!Boolean(documentos?.length)) {
          return toastWarning('Selecione ao menos um item');
        }
        const data = {
          especie_id: 10,
          documentos: documentos?.map((d) => d?.id),
        };
        postDocumentosGenerator({ data, cb: loadGrid });
      },
    },
    {
      name: 'Impressão de Etiquetas',
      icon: 'sell',
      action: (selected) => {
        if (Boolean(selected?.length)) {
          navigate('/app/Pedidos/Etiquetas', {
            state: { notas: selected?.map((s) => s?.id) },
          });
        } else {
          toastWarning('Selecione ao menos um item');
        }
      },
    },
    {
      name: 'Importar Pedido de Marketplace',
      icon: 'sync',
      show: tabPedido !== 'NORMAL',
      action: () => openModal(<ImportModal callback={loadGrid} />),
    },
  ];

  const rowActions = [
    {
      name: 'Aprovação necessária',
      icon: 'priority_high',
      color: 'secondary.main',
      show: ({ row }) => row?.aprovacao === 'PENDENTE',
    },
    {
      name: 'Aprovado',
      icon: 'check',
      color: 'primary.main',
      show: ({ row }) => row?.aprovacao === 'SIM',
    },
    {
      name: ' Reprovado',
      icon: 'close',
      color: 'secondary.main',
      show: ({ row }) => row?.aprovacao === 'NAO',
    },
    {
      name: 'Detalhes',
      icon: 'visibility',
      action: ({ row }) => navigate(`/app/Documento/${row?.id}`),
    },
    {
      name: 'Imprimir',
      icon: 'print',
      action: ({ row }) => print(row?.id),
      show: ({ row }) => !['NAO', 'PENDENTE']?.includes(row?.aprovacao),
    },
  ];

  return (
    <Container fillHeight>
      <Header rotina={rotina} colunas={pedidos?.colunas} />
      <Card fillHeight>
        <Box display="flex" flexDirection="column" gap={2} height="100%">
          <Tabs
            value={tabPedido}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={tabsSx}
          >
            {tabsPedido.map((tab) => (
              <Tab key={tab.value} value={tab.value} label={tab.label} />
            ))}
          </Tabs>
          <Divider />
          <DataView
            key={`${tabPedido}-${columnVisibilityKey}`}
            fillHeight
            grid={pedidosGrid}
            filterFields={filterFields}
            filterForm={filterForm}
            onFilter={applyFilter}
            columnOverrides={columnOverrides}
            toolbarActions={toolbarActions}
            rowActions={rowActions}
            checkboxSelection
            loading={getLoading}
            loadGrid={loadGrid}
          />
        </Box>
      </Card>
    </Container>
  );
};

export default Pedidos;
